import { pluralizeWithCount } from "@/lib/utils";
import { eqArrays } from "../../shared/utils";

export function filterMultiselectPaginated(args) {
  const {
    itemsName,
    onChange,
    onSelect,
    paginationEndpoint,
    pageSize = 200,
    getSelectedCount = null,
    shouldAutoSelect = () => false,
  } = args;

  const selectedIds = new Set(args.selectedIds);

  let originalValue, originalItems;

  return {
    selectedIds,
    disabledIds: new Set(),
    additions: new Set(),
    deletions: new Set(),
    dynamicPrompt: null,
    isOpen: false,
    items: args.items,
    searchTermPresent: false,
    itemsUpdating: false,
    currentSearchTerm: "",
    itemHeight: 40,
    visibleItems: [],
    scrollTop: 0,
    containerHeight: 288, // max-h-72 = 18rem = 288px
    currentOffset: args.items.length,
    hasMore: args.items.length === pageSize,
    isLoadingMore: false,
    init() {
      originalItems = this.items;
      this.items = this.sortedItems();
      this.updateVisibleItems();
      this.setPrompt();
    },
    get selectedIdsArr() {
      return Array.from(this.selectedIds);
    },
    toggleDropdown() {
      if (this.isOpen) {
        return this.unfocus();
      }

      this.focus();
    },
    focus() {
      this.isOpen = true;

      originalValue = this.selectedIdsArr;

      this.$nextTick(() => {
        this.$refs.searchInput.value = this.currentSearchTerm;
        this.$refs.searchInput?.focus();
      });

      this.items = this.sortedItems();
      this.updateVisibleItems();
    },
    unfocus() {
      this.isOpen = false;

      const currentValue = this.selectedIdsArr;
      if (!eqArrays(currentValue, originalValue)) {
        onChange(this);
      }
    },
    emitOnSelect() {
      const currentValue = this.selectedIdsArr;
      if (currentValue.length === 0) {
        onSelect(this.$root, []);
      } else {
        onSelect(this.$root, Array.from(currentValue));
      }
    },
    setPrompt() {
      const countToDisplay = getSelectedCount
        ? getSelectedCount()
        : this.selectedIds.size;

      if (countToDisplay > 0) {
        this.dynamicPrompt = pluralizeWithCount(itemsName, countToDisplay);
      } else {
        this.dynamicPrompt = null;
      }
    },
    selectItem(id) {
      this.selectedIds.add(id);
      this.emitOnSelect();
    },
    deselectItem(id) {
      this.selectedIds.delete(id);
      this.emitOnSelect();
    },
    isDisabled(id) {
      return this.disabledIds.has(id);
    },
    isSelected(id) {
      return this.selectedIds.has(id);
    },
    toggleSelectItem(id) {
      if (this.disabledIds.has(id)) return;

      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id);
        // If this item was added in this session, just remove it from additions
        // Don't add to deletions since it was never in the backend
        if (this.additions.has(id)) {
          this.additions.delete(id);
        } else {
          // Item was originally selected, so track it as a deletion
          this.deletions.add(id);
        }
      } else {
        this.selectedIds.add(id);
        // If this item was deleted in this session, just remove it from deletions
        // Don't add to additions since it was already in the backend
        if (this.deletions.has(id)) {
          this.deletions.delete(id);
        } else {
          // Item is being newly selected, so track it as an addition
          this.additions.add(id);
        }
      }

      this.emitOnSelect();

      this.setPrompt();
    },
    selectAll() {
      this.items.forEach((item) => {
        if (!this.selectedIds.has(item.id)) {
          this.toggleSelectItem(item.id);
        }
      });
    },
    deselectAll() {
      const selectedIdsArray = Array.from(this.selectedIds);
      selectedIdsArray.forEach((id) => {
        this.toggleSelectItem(id);
      });

      this.disabledIds.clear();
    },
    loadState(state) {
      this.selectedIds.clear();
      state.selectedIds.forEach((id) => this.selectedIds.add(id));

      this.additions.clear();
      state.additions.forEach((id) => this.additions.add(id));

      this.deletions.clear();
      state.deletions.forEach((id) => this.deletions.add(id));

      this.items = this.sortedItems();
      this.updateVisibleItems();
      this.setPrompt();
    },
    get additionsArr() {
      return Array.from(this.additions);
    },
    get deletionsArr() {
      return Array.from(this.deletions);
    },
    autoSelectNewItems(items) {
      if (!items || items.length === 0) return;

      items.forEach((item) => {
        // Only auto-select items that haven't been manually changed by user
        const isInAdditions = this.additions.has(item.id);
        const isInDeletions = this.deletions.has(item.id);

        if (!isInAdditions && !isInDeletions && shouldAutoSelect(item)) {
          this.selectedIds.add(item.id);
        }
      });
    },
    search(e) {
      const searchTerm = e.target.value.trim();

      // Don't make duplicate requests for the same search term
      if (searchTerm === this.currentSearchTerm) {
        return;
      }

      this.searchTermPresent = searchTerm.length > 0;
      this.currentSearchTerm = searchTerm;

      this.itemsUpdating = true;
      this.items = [];
      this.updateVisibleItems();

      // Reset pagination when searching
      this.currentOffset = 0;

      const searchUrl =
        searchTerm.length > 0
          ? `${paginationEndpoint}?q=${encodeURIComponent(searchTerm)}&limit=${pageSize}&offset=0`
          : `${paginationEndpoint}?limit=${pageSize}&offset=0`;

      fetch(searchUrl)
        .then((response) => response.json())
        .then((data) => {
          // Only update if this is still the current search term
          // (prevents race conditions with rapid typing)
          if (searchTerm === this.currentSearchTerm) {
            // Auto-select newly loaded items based on parent's selection logic
            this.autoSelectNewItems(data.resources);

            // Replace items (don't append) when searching
            originalItems = data.resources || [];

            // Update items (already sorted from backend)
            this.items = originalItems;
            this.updateVisibleItems();

            // Update pagination state
            this.currentOffset = originalItems.length;
            this.hasMore = data.has_more;
          }

          this.itemsUpdating = false;
        })
        .catch((error) => {
          console.error("Error searching:", error);
          this.itemsUpdating = false;
        });
    },
    sortedItems() {
      // Always sort by current selection state (items selected in this session)
      // Backend handles sorting by saved allowlisted status, but we need to
      // also sort by what user just selected/deselected in the current session
      return originalItems.sort((a, b) => {
        const aSelected = this.isSelected(a.id) ? 1 : 0;
        const bSelected = this.isSelected(b.id) ? 1 : 0;
        return bSelected - aSelected;
      });
    },
    updateVisibleItems() {
      const startIndex = Math.floor(this.scrollTop / this.itemHeight);
      const endIndex = Math.min(
        startIndex + Math.ceil(this.containerHeight / this.itemHeight) + 1,
        this.items.length,
      );

      this.visibleItems = this.items
        .slice(startIndex, endIndex)
        .map((item, index) => ({
          ...item,
          virtualIndex: startIndex + index,
          style: `transform: translateY(${(startIndex + index) * this.itemHeight}px); height: ${this.itemHeight}px;`,
        }));
    },
    get totalHeight() {
      return this.items.length * this.itemHeight;
    },
    handleScroll(e) {
      this.scrollTop = e.target.scrollTop;
      this.updateVisibleItems();

      // Check if we should load more items (infinite scroll)
      if (this.shouldLoadMore(e.target)) {
        this.loadNextPage();
      }
    },
    shouldLoadMore(scrollContainer) {
      // Only load more if there's more data to load and we're not already loading
      if (!this.hasMore || this.isLoadingMore) {
        return false;
      }

      // Check if user has scrolled near the bottom (within 200px)
      const scrollBottom =
        scrollContainer.scrollTop + scrollContainer.clientHeight;
      const scrollHeight = scrollContainer.scrollHeight;
      return scrollHeight - scrollBottom < 200;
    },
    loadNextPage() {
      if (!this.hasMore || this.isLoadingMore) {
        return;
      }

      this.isLoadingMore = true;

      // Get current search term from the search input
      const searchTerm = this.$refs.searchInput?.value || "";

      // Build URL with search term if present
      let url = `${paginationEndpoint}?offset=${this.currentOffset}&limit=${pageSize}`;
      if (searchTerm.length > 0) {
        url += `&q=${encodeURIComponent(searchTerm)}`;
      }

      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          if (data.resources && data.resources.length > 0) {
            // Auto-select newly loaded items based on parent's selection logic
            this.autoSelectNewItems(data.resources);

            // Append new items to originalItems
            originalItems = [...originalItems, ...data.resources];

            // Update items (don't re-sort, keep stable while dropdown is open)
            this.items = originalItems;
            this.updateVisibleItems();

            // Update pagination state
            this.currentOffset += data.resources.length;
            this.hasMore = data.has_more;
          } else {
            this.hasMore = false;
          }

          this.isLoadingMore = false;
        })
        .catch((error) => {
          console.error("Error loading more items:", error);
          this.isLoadingMore = false;
          this.hasMore = false;
        });
    },
  };
}
